import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, ShareNetwork, Flag, Copy, WhatsappLogo, Brain, ChartBar, Lock } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleLogin } from '@react-oauth/google';

import { Button, Spinner, Badge, ConfirmDialog } from '@/components/ui';
import { PollMeta } from '@/components/features/PollMeta/PollMeta';
import { PollResults } from '@/components/features/PollResults/PollResults';
import { PollActions } from '@/components/features/PollActions/PollActions';
import { CommentThread } from '@/components/features/CommentThread/CommentThread';
import { ReportDialog } from '@/components/features/ReportDialog/ReportDialog';
import { QuizResult } from '@/components/features/QuizResult/QuizResult';
import { usePoll, useCastVote, useRemoveVote, useDeletePoll } from '@/api';
import { useAuthStore } from '@/stores';
import { useSharePoll } from '@/hooks';
import { ROUTES } from '@/constants';

import styles from './PollDetail.module.css';

export function PollDetail() {
    const { pollSlug } = useParams<{ userSlug: string; pollSlug: string }>();
    const navigate = useNavigate();
    const { isLogged, isLoading: authLoading, getUser, loginWithGoogle } = useAuthStore();
    const user = getUser();
    const { share, shareWhatsApp } = useSharePoll();

    const { data: poll, isLoading, error } = usePoll(pollSlug || '');
    const castVote = useCastVote(pollSlug || '');
    const removeVote = useRemoveVote(pollSlug || '');
    const deletePoll = useDeletePoll();

    const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
    const [showReportDialog, setShowReportDialog] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [pendingVote, setPendingVote] = useState<number[] | null>(null);
    const loginSectionRef = useRef<HTMLDivElement>(null);

    const hasVoted = poll?.user_vote !== null && poll?.user_vote !== undefined && poll.user_vote.length > 0;
    const isOwner = poll && user && poll.author.id === user.id;

    // Quiz result logic
    const isQuiz = poll?.is_quiz ?? false;
    const correctOption = isQuiz && hasVoted
        ? poll?.options.find((o) => o.is_correct === true)
        : null;
    const userVotedCorrectly = isQuiz && hasVoted && correctOption
        ? (poll?.user_vote ?? []).includes(correctOption.id)
        : false;

    useEffect(() => {
        if (poll) {
            document.title = `${poll.title} — Ratio`;
        }
    }, [poll]);

    useEffect(() => {
        if (poll?.user_vote) {
            setSelectedOptions(poll.user_vote);
        }
    }, [poll?.user_vote]);

    // Auto-submit pending vote after login completes
    useEffect(() => {
        if (!isLogged || !pendingVote || hasVoted) return;
        castVote.mutate({ option_ids: pendingVote });
        setPendingVote(null);
        setShowLoginPrompt(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLogged, hasVoted]);

    // Scroll to login section when shown
    useEffect(() => {
        if (showLoginPrompt && loginSectionRef.current) {
            loginSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [showLoginPrompt]);

    const handleSelectOption = (optionId: number) => {
        if (hasVoted) return;

        if (poll?.poll_type === 'single_choice') {
            setSelectedOptions([optionId]);
        } else {
            setSelectedOptions((prev) =>
                prev.includes(optionId)
                    ? prev.filter((id) => id !== optionId)
                    : [...prev, optionId]
            );
        }
    };

    const handleVote = () => {
        if (selectedOptions.length === 0) {
            toast.error('Selecciona al menos una opcion');
            return;
        }

        if (!isLogged) {
            setPendingVote(selectedOptions);
            setShowLoginPrompt(true);
            return;
        }

        castVote.mutate({ option_ids: selectedOptions });
    };

    const handleGoogleSuccess = (credential: string) => {
        loginWithGoogle(credential);
    };

    const handleBack = () => {
        if (!hasVoted && poll?.is_active) {
            setShowLeaveConfirm(true);
        } else {
            navigate(ROUTES.feed);
        }
    };

    const handleRemoveVote = () => {
        removeVote.mutate(undefined, {
            onSuccess: () => {
                setSelectedOptions([]);
            },
        });
    };

    const handleDelete = () => {
        setShowDeleteConfirm(true);
    };

    const confirmDelete = () => {
        if (!poll) return;
        setShowDeleteConfirm(false);
        deletePoll.mutate(poll.slug, {
            onSuccess: () => navigate(ROUTES.feed, { replace: true }),
        });
    };

    const handleEdit = () => {
        if (!poll) return;
        if (poll.total_votes > 0) {
            toast.error('No puedes editar una encuesta que ya tiene votos.');
            return;
        }
        navigate(`${ROUTES.create}?edit=${poll.slug}`);
    };

    if (isLoading) {
        return (
            <div className={styles.loading}>
                <Spinner size="lg" />
            </div>
        );
    }

    if (error || !poll) {
        return (
            <div className={styles.error}>
                <p>No se encontro la encuesta</p>
                <Button variant="primary" onClick={() => navigate(ROUTES.feed)}>
                    Volver al inicio
                </Button>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title="Eliminar encuesta"
                message="Estas seguro que quieres eliminar esta encuesta? Esta accion no se puede deshacer."
                confirmLabel="Eliminar"
                cancelLabel="Cancelar"
                onConfirm={confirmDelete}
                onCancel={() => setShowDeleteConfirm(false)}
                variant="danger"
            />

            {isLogged && !isOwner && (
                <ReportDialog
                    isOpen={showReportDialog}
                    contentType="poll"
                    contentId={poll.id}
                    onClose={() => setShowReportDialog(false)}
                />
            )}

            <ConfirmDialog
                isOpen={showLeaveConfirm}
                title="Salir sin votar"
                message="¿Deseas ir al inicio sin haber votado?"
                confirmLabel="Ir al inicio"
                cancelLabel="Quedarme"
                onConfirm={() => navigate(ROUTES.feed)}
                onCancel={() => setShowLeaveConfirm(false)}
            />

            <button
                className={styles.backBtn}
                onClick={handleBack}
                aria-label="Volver"
            >
                <ArrowLeft size={18} />
                Volver
            </button>

            <div className={styles.content}>
                {/* Type badge */}
                <div className={styles.typeBadge}>
                    <Badge variant={isQuiz ? 'info' : 'default'}>
                        {isQuiz ? <><Brain size={14} weight="bold" /> Trivia</> : <><ChartBar size={14} weight="bold" /> Encuesta</>}
                    </Badge>
                </div>

                <h1 className={styles.title}>{poll.title}</h1>
                {poll.description && (
                    <p className={styles.description}>{poll.description}</p>
                )}

                <PollMeta poll={poll} />

                <div className={styles.actionRow}>
                    {isOwner && (
                        <PollActions
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            isDeleting={deletePoll.isPending}
                        />
                    )}
                    <div className={styles.secondaryActions}>
                        <button
                            className={styles.iconBtn}
                            onClick={() => share(poll.author.slug, poll.slug, poll.title)}
                            aria-label="Compartir encuesta"
                        >
                            <ShareNetwork size={18} />
                        </button>
                        {isLogged && !isOwner && (
                            <button
                                className={styles.iconBtn}
                                onClick={() => setShowReportDialog(true)}
                                aria-label="Reportar encuesta"
                            >
                                <Flag size={18} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Quiz result banner */}
                {isQuiz && hasVoted && correctOption && (
                    <QuizResult
                        isCorrect={userVotedCorrectly}
                        correctOptionText={correctOption.text}
                        userSlug={poll.author.slug}
                        pollSlug={poll.slug}
                        pollTitle={poll.title}
                    />
                )}

                <div className={styles.votingSection}>
                    {!poll.is_active && (
                        <div className={styles.closedBanner}>
                            <Lock size={18} weight="bold" />
                            <span>Esta encuesta esta cerrada y ya no acepta votos</span>
                        </div>
                    )}

                    <PollResults
                        poll={poll}
                        selectedOptions={selectedOptions}
                        onSelectOption={handleSelectOption}
                        hasVoted={hasVoted}
                        disabled={hasVoted || !poll.is_active}
                    />

                    {poll.is_active && !hasVoted && (
                        <div className={styles.voteActions}>
                            <motion.button
                                className={styles.voteBtn}
                                onClick={handleVote}
                                disabled={selectedOptions.length === 0 || castVote.isPending}
                                whileTap={{ scale: 0.97 }}
                            >
                                <span style={{ visibility: castVote.isPending ? 'hidden' : 'visible' }}>Votar</span>
                                {castVote.isPending && <Spinner size="sm" variant="inverse" className={styles.btnSpinner} />}
                            </motion.button>
                        </div>
                    )}

                    {/* Inline Google login after clicking Votar while anonymous */}
                    <AnimatePresence mode="wait">
                        {showLoginPrompt && !isLogged && (
                            <motion.div
                                key="login-prompt"
                                ref={loginSectionRef}
                                className={styles.loginPrompt}
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3, ease: 'easeOut' }}
                            >
                                {authLoading ? (
                                    <motion.div
                                        className={styles.loginLoading}
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <Spinner size="md" />
                                        <p className={styles.loginLoadingText}>Verificando cuenta...</p>
                                    </motion.div>
                                ) : (
                                    <>
                                        <p className={styles.loginPromptText}>
                                            Inicia sesion para registrar tu voto
                                        </p>
                                        <div className={styles.loginPromptButton}>
                                            <GoogleLogin
                                                onSuccess={(resp) => {
                                                    if (resp.credential) {
                                                        handleGoogleSuccess(resp.credential);
                                                    }
                                                }}
                                                onError={() => {
                                                    toast.error('No se pudo iniciar sesion. Intenta de nuevo.');
                                                }}
                                                shape="pill"
                                                size="large"
                                                text="signin_with"
                                                width="300"
                                            />
                                        </div>
                                    </>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Submitting vote after login */}
                    <AnimatePresence>
                        {pendingVote && isLogged && castVote.isPending && (
                            <motion.div
                                className={styles.loginPrompt}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <motion.div
                                    className={styles.loginLoading}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                >
                                    <Spinner size="md" />
                                    <p className={styles.loginLoadingText}>Registrando tu voto...</p>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {poll.is_active && hasVoted && (
                        <div className={styles.voteActions}>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleRemoveVote}
                                disabled={removeVote.isPending}
                            >
                                Quitar voto
                            </Button>
                        </div>
                    )}
                </div>

                {/* Voting stats */}
                <p className={styles.voteStat}>
                    {poll.total_votes} votos · {poll.total_comments} comentarios
                </p>

                {/* Share section after voting */}
                {hasVoted && (
                    <div className={styles.shareSection}>
                        <h3 className={styles.shareTitle}>Compartir</h3>
                        <div className={styles.shareButtons}>
                            <button
                                className={styles.shareBtn}
                                onClick={() => share(poll.author.slug, poll.slug, poll.title)}
                            >
                                <Copy size={18} weight="bold" />
                                Copiar enlace
                            </button>
                            <button
                                className={styles.whatsappBtn}
                                onClick={() => shareWhatsApp(poll.author.slug, poll.slug, poll.title)}
                            >
                                <WhatsappLogo size={18} weight="fill" />
                                WhatsApp
                            </button>
                        </div>
                    </div>
                )}

                <div className={styles.divider} />

                <CommentThread pollSlug={poll.slug} />
            </div>
        </div>
    );
}
